import {
  THRILLION_SUPPORT_ROLE_IDS,
  isThrillionSupportStaffRole,
  getDefaultRouteByRole,
} from "./roleRedirect";

/** Mirrors backend `user_model.models.User` role ints where relevant. */
export const ROLE = {
  SCHOOL_HEAD: 1,
  SCHOOL_ADMIN: 2,
  FACULTY: 3,
  CLASS_TEACHER: 4,
  THRILLION_SUPPORT: 7,
  SUPER_ADMIN: 8,
  CLERK: 9,
  LUXEHARA: 11,
  SUPPORT_LEAD: 13,
  SUPPORT_MANAGER: 14,
  VENDOR_ADMIN: 15,
  VENDOR_STAFF: 16,
};

const SCHOOL_HEAD_ADMIN = [ROLE.SCHOOL_HEAD, ROLE.SCHOOL_ADMIN];
/** School head, admin, or clerk (transport / office). */
const SCHOOL_HEAD_ADMIN_OR_CLERK = [ROLE.SCHOOL_HEAD, ROLE.SCHOOL_ADMIN, ROLE.CLERK];
const FACULTY_LIKE = [ROLE.FACULTY, ROLE.CLASS_TEACHER];
const THRILLION_SUPPORT = [...THRILLION_SUPPORT_ROLE_IDS];
const SUPER_ADMIN_ONLY = [ROLE.SUPER_ADMIN];
const LUXEHARA_ONLY = [ROLE.LUXEHARA];
const VENDOR_ADMIN_ONLY = [ROLE.VENDOR_ADMIN];
const VENDOR_ORG_MEMBERS = [ROLE.VENDOR_ADMIN, ROLE.VENDOR_STAFF];
/** Thrillion support + Vendor Staff — school onboarding workflow only. */
const STAFF_ONBOARDING = [...THRILLION_SUPPORT, ROLE.VENDOR_STAFF];
const STAFF_ONBOARDING_WITH_SUPER = [...STAFF_ONBOARDING, ROLE.SUPER_ADMIN];

/**
 * Normalize API/Redux role to a numeric id when possible.
 */
export function normalizeRoleId(roleValue) {
  const n = Number(roleValue);
  if (!Number.isNaN(n) && roleValue !== "" && roleValue !== null && roleValue !== undefined) {
    return n;
  }
  const t = String(roleValue ?? "")
    .toLowerCase()
    .replace(/\s+/g, "_");
  const map = {
    schoolhead: ROLE.SCHOOL_HEAD,
    school_head: ROLE.SCHOOL_HEAD,
    principal: ROLE.SCHOOL_HEAD,
    schooladmin: ROLE.SCHOOL_ADMIN,
    school_admin: ROLE.SCHOOL_ADMIN,
    admin: ROLE.SCHOOL_ADMIN,
    faculty: ROLE.FACULTY,
    class_teacher: ROLE.CLASS_TEACHER,
    classteacher: ROLE.CLASS_TEACHER,
    super_admin: ROLE.SUPER_ADMIN,
    superadmin: ROLE.SUPER_ADMIN,
    luxehara: ROLE.LUXEHARA,
    thrillionite_customer_support: ROLE.THRILLION_SUPPORT,
    thrillionite_customer_support_lead: ROLE.SUPPORT_LEAD,
    thrillionite_customer_support_manager: ROLE.SUPPORT_MANAGER,
    clerk: ROLE.CLERK,
    vendor_admin: ROLE.VENDOR_ADMIN,
    vendoradmin: ROLE.VENDOR_ADMIN,
    vendor_staff: ROLE.VENDOR_STAFF,
    vendorstaff: ROLE.VENDOR_STAFF,
  };
  return map[t] ?? NaN;
}

function matchesThrillionSupportAllowed(allowed, roleValue) {
  const needsSupport =
    allowed.includes(ROLE.THRILLION_SUPPORT) ||
    allowed.includes(ROLE.SUPPORT_LEAD) ||
    allowed.includes(ROLE.SUPPORT_MANAGER);
  if (!needsSupport) return false;
  return isThrillionSupportStaffRole(roleValue);
}

/**
 * @param {string} pathname - `location.pathname` (no query)
 * @param {unknown} roleValue - `userData.data.role` from auth API
 * @param {'all'|number[]} allowed
 */
export function roleMatchesAllowed(roleValue, allowed) {
  if (allowed === "all") return true;
  const n = normalizeRoleId(roleValue);
  if (!Number.isNaN(n) && allowed.includes(n)) return true;
  if (matchesThrillionSupportAllowed(allowed, roleValue)) return true;
  return false;
}

/**
 * Ordered rules: first match wins.
 * @type {{ pattern: RegExp, roles: 'all'|number[] }[]}
 */
export const PRIVATE_ROUTE_ACCESS_RULES = [
  { pattern: /^\/luxehara(\/|$)/, roles: LUXEHARA_ONLY },

  { pattern: /^\/super-admin\/billing/, roles: [ROLE.SUPER_ADMIN, ROLE.SUPPORT_MANAGER] },
  { pattern: /^\/super-admin\/hr-payroll\/?$/, roles: SUPER_ADMIN_ONLY },
  { pattern: /^\/super-admin\/sidebar-layout\/?$/, roles: SUPER_ADMIN_ONLY },
  { pattern: /^\/super-admins-dashboard\/?$/, roles: SUPER_ADMIN_ONLY },
  { pattern: /^\/register-thrillion-staff\/?$/, roles: SUPER_ADMIN_ONLY },
  { pattern: /^\/view-thrillion-staff\/?$/, roles: SUPER_ADMIN_ONLY },
  { pattern: /^\/thrillion-staff-profile\//, roles: SUPER_ADMIN_ONLY },
  { pattern: /^\/manage-subscription-plans\/?$/, roles: SUPER_ADMIN_ONLY },
  { pattern: /^\/super-admin\/school-plans\/?$/, roles: SUPER_ADMIN_ONLY },
  { pattern: /^\/super-admin\/add-blogs\/?$/, roles: SUPER_ADMIN_ONLY },
  { pattern: /^\/super-admin\/add-news-articles\/?$/, roles: SUPER_ADMIN_ONLY },
  { pattern: /^\/super-admin\/role-permissions\/?$/, roles: [ROLE.SUPER_ADMIN, ROLE.SUPPORT_MANAGER] },
  { pattern: /^\/super-admin\/vendor-organizations\/?$/, roles: SUPER_ADMIN_ONLY },
  { pattern: /^\/vendor\/dashboard\/?$/, roles: VENDOR_ORG_MEMBERS },
  { pattern: /^\/vendor\/staff\/?$/, roles: VENDOR_ADMIN_ONLY },

  { pattern: /^\/demo-requests\/?$/, roles: [...THRILLION_SUPPORT, ROLE.SUPER_ADMIN] },
  { pattern: /^\/all-schools\/?$/, roles: STAFF_ONBOARDING_WITH_SUPER },

  { pattern: /^\/application-testing(\/ticket-details)?\/?$/, roles: [...THRILLION_SUPPORT, ROLE.SUPER_ADMIN] },

  { pattern: /^\/staff-dashboard\/?$/, roles: STAFF_ONBOARDING },
  { pattern: /^\/support-lead\/tickets\/?$/, roles: [...THRILLION_SUPPORT, ROLE.SUPER_ADMIN] },
  { pattern: /^\/register-school-admins\/?$/, roles: STAFF_ONBOARDING },
  { pattern: /^\/view-all-school-admins\/?$/, roles: STAFF_ONBOARDING },
  { pattern: /^\/onboard\/?$/, roles: STAFF_ONBOARDING },

  { pattern: /^\/manage-schools\/?$/, roles: STAFF_ONBOARDING_WITH_SUPER },
  { pattern: /^\/school-info\/?$/, roles: STAFF_ONBOARDING_WITH_SUPER },
  { pattern: /^\/school-website\/?$/, roles: SCHOOL_HEAD_ADMIN },
  { pattern: /^\/admissions-enquiry\/?$/, roles: SCHOOL_HEAD_ADMIN_OR_CLERK },
  { pattern: /^\/app-billing\/?$/, roles: SCHOOL_HEAD_ADMIN },

  { pattern: /^\/faculty-dashboard\/?$/, roles: FACULTY_LIKE },
  { pattern: /^\/faculty\/view-tasks\/?$/, roles: FACULTY_LIKE },

  { pattern: /^\/faculty\/add-diary\/?$/, roles: FACULTY_LIKE },
  { pattern: /^\/faculty\/view-diary\/?$/, roles: FACULTY_LIKE },

  {
    pattern: /^\/study-material\/?$/,
    roles: [...SCHOOL_HEAD_ADMIN, ROLE.CLERK, ...FACULTY_LIKE],
  },
  {
    pattern: /^\/my-todos\/?$/,
    roles: [],
  },

  { pattern: /^\/transportation-system\/?$/, roles: SCHOOL_HEAD_ADMIN_OR_CLERK },
  { pattern: /^\/dashboard\/?$/, roles: SCHOOL_HEAD_ADMIN },
  { pattern: /^\/clerk-dashboard\/?$/, roles: [ROLE.CLERK] },

  { pattern: /^\/faculty-leave-details\/?$/, roles: [...SCHOOL_HEAD_ADMIN, ...FACULTY_LIKE] },
  { pattern: /^\/add-attendance-page\/?$/, roles: [...SCHOOL_HEAD_ADMIN, ...FACULTY_LIKE] },
  { pattern: /^\/view-attendance-page\/?$/, roles: SCHOOL_HEAD_ADMIN },
  { pattern: /^\/view-attendance-report\/?$/, roles: SCHOOL_HEAD_ADMIN },
  { pattern: /^\/add-individual-subject-marks\/?$/, roles: [...SCHOOL_HEAD_ADMIN, ...FACULTY_LIKE] },
  { pattern: /^\/marks-sheet\/?$/, roles: [...SCHOOL_HEAD_ADMIN, ...FACULTY_LIKE] },
  { pattern: /^\/faculty\/add-syllabus-planning\/?$/, roles: FACULTY_LIKE },
  {
    pattern: /^\/faculty\/view-syllabus-planning\/?$/,
    roles: [...SCHOOL_HEAD_ADMIN, ...FACULTY_LIKE],
  },
  { pattern: /^\/view-syllabus\/?$/, roles: SCHOOL_HEAD_ADMIN },
  { pattern: /^\/add-plan\/?$/, roles: [...SCHOOL_HEAD_ADMIN, ...FACULTY_LIKE] },
  { pattern: /^\/action-plan\//, roles: [...SCHOOL_HEAD_ADMIN, ...FACULTY_LIKE] },
  { pattern: /^\/assign-subjects-to-examination\/?$/, roles: SCHOOL_HEAD_ADMIN },
  { pattern: /^\/add-complaint\/?$/, roles: [...SCHOOL_HEAD_ADMIN, ...FACULTY_LIKE] },

  { pattern: /^\/diary-info\/?$/, roles: SCHOOL_HEAD_ADMIN },
  { pattern: /^\/view-teaching-plans\/?$/, roles: SCHOOL_HEAD_ADMIN },
  { pattern: /^\/create-class-time-table\/?$/, roles: SCHOOL_HEAD_ADMIN },
  {
    pattern: /^\/view-class-time-table\/?$/,
    roles: [ROLE.FACULTY, ROLE.CLASS_TEACHER, ROLE.CLERK],
  },
  { pattern: /^\/add-student\/?$/, roles: SCHOOL_HEAD_ADMIN_OR_CLERK },
  { pattern: /^\/view-all-students\/?$/, roles: SCHOOL_HEAD_ADMIN_OR_CLERK },
  { pattern: /^\/student-promotion\/?$/, roles: SCHOOL_HEAD_ADMIN },
  { pattern: /^\/alumni\/?$/, roles: SCHOOL_HEAD_ADMIN },
  { pattern: /^\/view-student-profile\/?$/, roles: SCHOOL_HEAD_ADMIN_OR_CLERK },
  { pattern: /^\/edit-student\//, roles: SCHOOL_HEAD_ADMIN_OR_CLERK },
  { pattern: /^\/add-faculty\/?$/, roles: SCHOOL_HEAD_ADMIN },
  { pattern: /^\/view-all-faculty\/?$/, roles: SCHOOL_HEAD_ADMIN },
  { pattern: /^\/view-all-faculty\/.+/, roles: SCHOOL_HEAD_ADMIN },
  { pattern: /^\/calendar\/?$/, roles: SCHOOL_HEAD_ADMIN },
  { pattern: /^\/daily-updates\/?$/, roles: SCHOOL_HEAD_ADMIN },
  { pattern: /^\/my-school-settings\/?$/, roles: SCHOOL_HEAD_ADMIN },
  { pattern: /^\/knowledge-base\/?$/, roles: SCHOOL_HEAD_ADMIN },
  { pattern: /^\/knowledge-base\/[^/]+\/?$/, roles: SCHOOL_HEAD_ADMIN },
  { pattern: /^\/academic-year\/?$/, roles: SCHOOL_HEAD_ADMIN },
  { pattern: /^\/school-leave-policy\/?$/, roles: SCHOOL_HEAD_ADMIN },
  { pattern: /^\/add-classes\/?$/, roles: SCHOOL_HEAD_ADMIN },
  { pattern: /^\/assign-fee\/?$/, roles: SCHOOL_HEAD_ADMIN },
  { pattern: /^\/add-sections\/?$/, roles: SCHOOL_HEAD_ADMIN },
  { pattern: /^\/add-subjects\/?$/, roles: SCHOOL_HEAD_ADMIN },
  { pattern: /^\/add-examinations\/?$/, roles: SCHOOL_HEAD_ADMIN },
  { pattern: /^\/view-classes\/?$/, roles: SCHOOL_HEAD_ADMIN },
  { pattern: /^\/assign-fees\/?$/, roles: SCHOOL_HEAD_ADMIN },
  { pattern: /^\/fee-details-page\/?$/, roles: SCHOOL_HEAD_ADMIN_OR_CLERK },
  { pattern: /^\/fee-arrears\/?$/, roles: SCHOOL_HEAD_ADMIN_OR_CLERK },
  { pattern: /^\/fees-info\/?$/, roles: SCHOOL_HEAD_ADMIN_OR_CLERK },
  { pattern: /^\/fee-reminders\/?$/, roles: SCHOOL_HEAD_ADMIN_OR_CLERK },
  { pattern: /^\/collect-fees\/?$/, roles: SCHOOL_HEAD_ADMIN_OR_CLERK },
  { pattern: /^\/expense-lodger\/?$/, roles: SCHOOL_HEAD_ADMIN_OR_CLERK },
  { pattern: /^\/employee-payroll\/?$/, roles: SCHOOL_HEAD_ADMIN },
  {
    pattern: /^\/my-payslips\/?$/,
    roles: [ROLE.FACULTY, ROLE.CLASS_TEACHER, ROLE.CLERK, 7, 13, 14],
  },
  { pattern: /^\/school-leave-requests-info\/?$/, roles: SCHOOL_HEAD_ADMIN },
  { pattern: /^\/assign-tasks\/?$/, roles: SCHOOL_HEAD_ADMIN },
  { pattern: /^\/school-admin-tickets\/?$/, roles: SCHOOL_HEAD_ADMIN },
  { pattern: /^\/announcements\/?$/, roles: SCHOOL_HEAD_ADMIN_OR_CLERK },
  {
    pattern: /^\/ticket-info\/?$/,
    roles: [...SCHOOL_HEAD_ADMIN, ...THRILLION_SUPPORT, ROLE.SUPER_ADMIN],
  },

  { pattern: /^\/profile\/?$/, roles: "all" },
  { pattern: /^\/settings\/?$/, roles: "all" },
  { pattern: /^\/learn-thrillion\/home\/?$/, roles: "all" },
];

export function normalizePathname(pathname) {
  const p = (pathname || "/").replace(/\/$/, "");
  return p || "/";
}

export function isPathAllowedForRole(pathname, roleValue) {
  const path = normalizePathname(pathname);
  for (const rule of PRIVATE_ROUTE_ACCESS_RULES) {
    if (rule.pattern.test(path)) {
      return roleMatchesAllowed(roleValue, rule.roles);
    }
  }
  return false;
}

/**
 * Avoid redirect loops if the default home is also forbidden for this role.
 */
export function getSafeRedirectForRole(roleValue) {
  const primary = getDefaultRouteByRole(roleValue);
  if (isPathAllowedForRole(primary, roleValue)) return primary;
  if (isPathAllowedForRole("/profile", roleValue)) return "/profile";
  return primary;
}
